import type { BranchEmployeesData } from './team-types';

/** Minimal-Sicht auf den Ansprechpartner — strukturell aus `SalesRepresentative` zuweisbar. */
interface RepIdentity {
  name?: string | null;
  email?: string | null;
}

/** Normalisiert einen Namen für den Abgleich: trim, Mehrfach-Spaces zu einem, lowercase. */
function normalizeName(name: string | null | undefined): string | null {
  const n = name?.trim().replace(/\s+/g, ' ').toLowerCase();
  return n || null;
}

/**
 * Liefert das Verkäufer-Foto zum Ansprechpartner aus den statischen Stammdaten.
 *
 * Das Backend (`SalesRepresentativeService`) kann kein Foto liefern (NAV hat keine
 * Bilder, `photoUrl` ist dort immer `null`); die kuratierten Fotos leben ausschließlich
 * client-seitig in `branch-employees.json`. Diese Helper-Funktion ist die Brücke
 * zwischen dem aus NAV gelesenen Rep und seinem Foto.
 *
 * Abgleich in zwei Stufen:
 *   1. Verkaufs-Teams (`branches`) — zuverlässig per E-Mail (jedes Teammitglied hat eine).
 *   2. Sonder-Ansprechpartner (`additionalContacts`, z.B. Geschäftsführung) — per E-Mail
 *      ODER Name, da solche Kontakte öffentlich oft keine E-Mail hinterlegt haben (NAV
 *      liefert aber den Namen).
 *
 * Gibt die `photoUrl` nur zurück, wenn ein echtes Foto vorliegt (`hasPhoto`). Bei reiner
 * Blanko-Silhouette oder fehlendem Treffer `null`, damit die kompakten Avatare auf den
 * Initialen-Fallback zurückfallen. Die große Team-Karte rendert dagegen `emp.photoUrl` direkt.
 */
export function resolveRepPhotoUrl(
  data: BranchEmployeesData,
  rep: RepIdentity | null | undefined,
): string | null {
  const email = rep?.email?.trim().toLowerCase() || null;
  const name = normalizeName(rep?.name);

  // 1) Verkaufs-Team per E-Mail.
  if (email) {
    for (const branch of Object.values(data.branches)) {
      const match = branch.employees.find((e) => e.email.trim().toLowerCase() === email);
      if (match) return match.hasPhoto ? match.photoUrl : null;
    }
  }

  // 2) Sonder-Ansprechpartner per E-Mail oder Name.
  for (const contact of data.additionalContacts ?? []) {
    const emailHit = !!email && !!contact.email && contact.email.trim().toLowerCase() === email;
    const nameHit = !!name && normalizeName(contact.name) === name;
    if (emailHit || nameHit) return contact.hasPhoto ? contact.photoUrl : null;
  }

  return null;
}
